import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { MdAdd, MdClose } from 'react-icons/md';
import StatsCard from '@/components/StatsCard';
import StatusBadge from '@/components/StatusBadge';
import ViewButton from '@/components/ViewButton';
import CaseViewModal, { type CaseRow } from '@/components/CaseViewModal';
import { DataTable } from '@podus/ui';
import type { Column } from '@podus/ui';
import { api } from '@/lib/api';
import { exportCSV } from '@/lib/csv';
import { useYear } from '@/context/YearContext';
import type { Case, CaseStats } from '@/types';

/* ─── Constants ─── */

const TABS = ['All', 'New', 'Assigned', 'In Progress', 'Resolved'];

const STAT_LABELS = ['New', 'Assigned', 'In Progress', 'Resolved'] as const;

/* ─── Helpers ─── */

function mapToRow(c: Case, index: number): CaseRow {
    const applicant = c.parties?.find((p) => p.role === 'Applicant' || p.role === 'Claimant');
    return {
        id: c.id,
        sn: index + 1,
        caseNumber: c.caseNumber,
        date: new Date(c.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        complainantName: applicant?.name ?? c.caseNumber,
        caseCategory: c.caseCategory,
        disputeCategory: c.disputeCategory,
        evidence: c.evidence?.length ?? 'Nil',
        amountPaid: `₦${Number(c.amountPaid).toLocaleString()}`,
        status: c.status,
    };
}

/* ─── Component ─── */

export default function CaseManagerCaseManagement() {
    const navigate = useNavigate();
    const { yearQuery } = useYear();
    const [activeTab, setActiveTab] = useState('All');
    const [cases, setCases] = useState<CaseRow[]>([]);
    const [stats, setStats] = useState<CaseStats | null>(null);
    const [selectedCase, setSelectedCase] = useState<CaseRow | null>(null);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [selectedType, setSelectedType] = useState('walk-in');
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    useEffect(() => {
        if (!popoverOpen) return;
        const handler = (e: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
                setPopoverOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [popoverOpen]);

    const fetchCases = useCallback(async () => {
        setLoading(true);
        try {
            const statusParam = activeTab !== 'All' ? `&status=${encodeURIComponent(activeTab)}` : '';
            const searchParam = debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : '';
            const res = await api.get<Case[]>(`/api/cases${yearQuery}&limit=100${statusParam}${searchParam}`);
            const data = res.response.data ?? [];
            setCases(data.map(mapToRow));
        } catch (err) {
            console.error('Fetch Cases Error:', err);
            setCases([]);
        } finally {
            setLoading(false);
        }
    }, [activeTab, yearQuery, debouncedSearch]);

    const fetchStats = useCallback(async () => {
        try {
            const res = await api.get<CaseStats>(`/api/cases/stats${yearQuery}`);
            setStats(res.response.data ?? null);
        } catch (err) {
            console.error('Fetch Stats Error:', err);
        }
    }, [yearQuery]);

    useEffect(() => {
        fetchCases();
    }, [fetchCases]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const handleCaseUpdated = () => {
        fetchCases();
        fetchStats();
    };

    const statsCards = STAT_LABELS.map((label) => ({
        label,
        value: String(stats?.[label as keyof CaseStats] ?? 0),
    }));

    const COLUMNS: Column<CaseRow>[] = [
        { key: 'sn', label: 'S/N' },
        { key: 'caseNumber', label: 'Case Number' },
        { key: 'date', label: 'Date' },
        { key: 'complainantName', label: 'Complainant Name' },
        { key: 'caseCategory', label: 'Case Category' },
        { key: 'disputeCategory', label: 'Dispute Category' },
        { key: 'amountPaid', label: 'Amount Paid' },
        {
            key: 'status',
            label: 'Case Status',
            render: (row) => <StatusBadge status={row.status} />,
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (row) => <ViewButton onClick={() => setSelectedCase(row)} />,
        },
    ];

    return (
        <div className="space-y-6  py-6">
            <div className="flex flex-col gap-4 px-4 lg:flex-row lg:items-start lg:justify-between lg:px-8">
                <h1 className="text-2xl font-bold text-secondary sm:text-3xl">Case Management</h1>
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
                    {statsCards.map((s) => (
                        <StatsCard key={s.label} stat={s} />
                    ))}
                </div>
            </div>

            <DataTable
                title="Cases"
                columns={COLUMNS}
                data={cases}
                getRowKey={(row) => row.id}
                tabs={TABS}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                searchValue={searchInput}
                onSearchChange={setSearchInput}
                onExportCSV={() => exportCSV('cases', COLUMNS, cases)}
                headerActions={
                    <div className="relative" ref={popoverRef}>
                        <button
                            type="button"
                            onClick={() => setPopoverOpen((prev) => !prev)}
                            className="flex items-center gap-2 rounded-lg bg-secondary px-4 py-2 text-[14px] font-medium text-white transition-colors hover:bg-secondary-400"
                        >
                            <MdAdd size={16} />
                            Create New Case
                        </button>

                        {popoverOpen && (
                            <div className="absolute right-0 top-full z-30 mt-2 w-64 rounded-xl border border-[#eee] bg-white p-5 shadow-lg">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-[16px] font-bold text-secondary">Create New Case</h3>
                                    <button
                                        type="button"
                                        title="Close"
                                        onClick={() => setPopoverOpen(false)}
                                        className="text-[#999] hover:text-secondary"
                                    >
                                        <MdClose size={18} />
                                    </button>
                                </div>

                                <div className="mt-4 space-y-2">
                                    {[
                                        { label: 'Walk-in Case', value: 'walk-in' },
                                        { label: 'Court Referral', value: 'court-referral' },
                                    ].map((option) => (
                                        <label
                                            key={option.value}
                                            className="flex cursor-pointer items-center gap-3 py-2 text-[15px] text-secondary"
                                        >
                                            <input
                                                type="radio"
                                                name="case-type"
                                                checked={selectedType === option.value}
                                                onChange={() => setSelectedType(option.value)}
                                                className="sr-only"
                                            />
                                            <div
                                                className={`flex h-4 w-4 items-center justify-center rounded-full border transition-all ${
                                                    selectedType === option.value ? 'border-primary' : 'border-[#ccc]'
                                                }`}
                                            >
                                                <div
                                                    className={`h-2 w-2 rounded-full transition-all ${
                                                        selectedType === option.value
                                                            ? 'bg-primary scale-100'
                                                            : 'bg-transparent scale-0'
                                                    }`}
                                                />
                                            </div>
                                            {option.label}
                                        </label>
                                    ))}
                                </div>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setPopoverOpen(false);
                                        navigate(`/case-manager/cases/new?type=${selectedType}`);
                                    }}
                                    className="mt-4 flex w-full items-center justify-center rounded-xl bg-secondary py-3 text-[15px] font-bold text-white transition-all hover:bg-secondary-400"
                                >
                                    Proceed
                                </button>
                            </div>
                        )}
                    </div>
                }
            />

            <CaseViewModal
                open={!!selectedCase}
                onClose={() => setSelectedCase(null)}
                caseData={selectedCase}
                onCaseUpdated={handleCaseUpdated}
            />
        </div>
    );
}
